//changed due to package upgrade
import React, { useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import AppTextField from '@projectK/components/AppTextField';
import jwtAxios from '@projectK/services/auth/JWT';
import PropTypes from 'prop-types';

const WaitlistModal = ({
  open,
  onClose,
  productName,
  productId,
  userEmail,
  userName,
  companyTheme,
  infoViewActionsContext,
  userMobile,
  userId,
}) => {
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const validationSchema = Yup.object({
    name: Yup.string().trim().required('Name is required'),
    email: Yup.string()
      .trim()
      .email('Please enter a valid email')
      .required('Email is required'),
    phone: Yup.string(),
  });

  const initialValues = {
    name: userName || '',
    email: userEmail || '',
    phone: userMobile || '',
  };

  const handleClose = () => {
    setSubmitted(false);
    setSubmittedEmail('');
    onClose();
  };

  const primaryColor = companyTheme?.primary?.main || '#00897B';

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby='waitlist-modal-title'
    >
      <Box
        className='p-popup'
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          boxShadow: 24,
          borderRadius: 2,
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            pb: 2,
            borderBottom: submitted ? 'none' : '1px solid #e0e0e0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon sx={{ color: primaryColor, fontSize: 28 }} />
            <Typography
              id='waitlist-modal-title'
              variant='h5'
              component='h2'
              sx={{ fontWeight: 600 }}
            >
              {submitted
                ? "You're on the List!"
                : `Join ${productName} Waitlist`}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            size='small'
            sx={{
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {submitted ? (
            // Success State
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircleIcon
                sx={{
                  fontSize: 64,
                  color: primaryColor,
                  mb: 2,
                }}
              />
              <Typography variant='h6' sx={{ mb: 1, fontWeight: 500 }}>
                Thank You!
              </Typography>
              <Typography variant='body1' color='text.secondary' sx={{ mb: 2 }}>
                We'll notify you at <strong>{submittedEmail}</strong> when spots
                become available for {productName}.
              </Typography>
              <Alert severity='success' sx={{ mt: 2 }}>
                You'll be among the first to know when registration opens!
              </Alert>
            </Box>
          ) : (
            // Form
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={async (values, { setSubmitting }) => {
                try {
                  setSubmitting(true);
                  // Add your API call here
                  await jwtAxios.post('/products/out/of/stock/product-waitlist/', {
                    product_id: productId,
                    product_name: productName,
                    name: values.name,
                    email: values.email,
                    phone: values.phone,
                    user_id: userId,
                  });
                  setSubmittedEmail(values.email);
                  setSubmitted(true);
                  if (infoViewActionsContext) {
                    infoViewActionsContext.showMessage(
                      `Successfully joined waitlist for ${productName}`,
                    );
                  }
                } catch (error) {
                  if (infoViewActionsContext) {
                    infoViewActionsContext.fetchError(
                      error?.response?.data?.error || 'Failed to join waitlist',
                    );
                  }
                } finally {
                  setSubmitting(true);
                }
              }}
              enableReinitialize
            >
              {({ isSubmitting }) => (
                <Form>
                  <Typography
                    variant='body1'
                    color='text.secondary'
                    sx={{ mb: 3 }}
                  >
                    Join the waitlist to be included if space becomes available for this activity. Your information will be reviewed should openings occur.
                  </Typography>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <AppTextField
                      name='name'
                      variant='outlined'
                      sx={{ width: '100%', mb: 2.5 }}
                      label='Full Name'
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <AppTextField
                      name='email'
                      variant='outlined'
                      sx={{ width: '100%', mb: 2.5 }}
                      label='Email Address'
                      disabled
                      type='email'
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <AppTextField
                      name='phone'
                      variant='outlined'
                      sx={{ width: '100%', mb: 2.5 }}
                      label='Phone Number (Optional)'
                      type='tel'
                      inputProps={{ placeholder: '(123) 456-7890' }}
                    />
                  </Grid>

                  <Box
                    sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}
                  >
                    <Button
                      variant='outlined'
                      onClick={handleClose}
                      type='button'
                      disabled={isSubmitting}
                      sx={{
                        minWidth: 100,
                        borderColor: primaryColor,
                        color: primaryColor,
                        '&:hover': {
                          borderColor: primaryColor,
                          bgcolor: `${primaryColor}08`,
                        },
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type='submit'
                      variant='contained'
                      disabled={isSubmitting}
                      sx={{
                        minWidth: 140,
                        bgcolor: primaryColor,
                        '&:hover': {
                          bgcolor: primaryColor,
                          opacity: 0.9,
                        },
                      }}
                    >
                      {isSubmitting ? 'Joining...' : 'Join Waitlist'}
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default WaitlistModal;

WaitlistModal.propTypes = {
  open: PropTypes.func,
  onClose: PropTypes.func,
  productName: PropTypes.string,
  productId: PropTypes.number,
  userEmail: PropTypes.string,
  userName: PropTypes.string,
  companyTheme: PropTypes.object,
  infoViewActionsContext: PropTypes.func,
  userMobile: PropTypes.string,
  userId: PropTypes.number,
};
